import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createCustomer, updateCustomer, getCustomerById } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Phone, Home, Mail, AlertCircle, FileText, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layouts/DashboardLayout';

// Esquema de validação com Zod
const formSchema = z.object({
  name: z.string().min(2, { message: 'Nome é obrigatório e deve ter pelo menos 2 caracteres' }),
  phone: z.string()
    .min(10, { message: 'Telefone deve ter pelo menos 10 dígitos' })
    .regex(/^\d+$/, { message: 'Telefone deve conter apenas números' }),
  email: z.string().email({ message: 'Email inválido' }).optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  postal_code: z.string().optional().or(z.literal('')),
  customer_type: z.enum(['pessoa_fisica', 'pessoa_juridica']).default('pessoa_fisica'),
  document: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  birthday: z.string().optional().or(z.literal('')),
});

type FormValues = z.infer<typeof formSchema>;

const CustomerForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [activeTab, setActiveTab] = useState('personal');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      customer_type: 'pessoa_fisica',
      document: '',
      notes: '',
      birthday: '',
    }
  });

  useEffect(() => {
    const loadCustomer = async () => {
      if (isEditing && id) {
        try {
          setInitialLoading(true);
          const customer = await getCustomerById(id);
          
          if (customer) {
            form.reset({
              name: customer.name || '',
              phone: customer.phone || '',
              email: customer.email || '',
              address: customer.address || '',
              city: customer.city || '',
              state: customer.state || '',
              postal_code: customer.postal_code || '',
              customer_type: customer.customer_type || 'pessoa_fisica',
              document: customer.document || '',
              notes: customer.notes || '',
              birthday: customer.birthday || '',
            });
          } else {
            toast.error('Cliente não encontrado');
            navigate('/dashboard/customers');
          }
        } catch (error) {
          console.error('Erro ao carregar cliente:', error);
          toast.error('Erro ao carregar dados do cliente');
        } finally {
          setInitialLoading(false);
        }
      }
    };

    loadCustomer();
  }, [id, isEditing, navigate, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);
      if (isEditing && id) {
        await updateCustomer(id, data);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await createCustomer(data);
        toast.success('Cliente criado com sucesso!');
      }
      navigate('/dashboard/customers');
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      toast.error('Erro ao salvar cliente');
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <DashboardLayout title="Carregando...">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="mt-2 text-muted-foreground">Carregando dados do cliente...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={isEditing ? 'Editar Cliente' : 'Novo Cliente'}>
      <div className="container px-0">
        <div className="flex items-center mb-6">
          <Button variant="outline" size="icon" onClick={() => navigate('/dashboard/customers')} className="mr-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid grid-cols-3 mb-4">
                    <TabsTrigger value="personal">Dados Pessoais</TabsTrigger>
                    <TabsTrigger value="address">Endereço</TabsTrigger>
                    <TabsTrigger value="additional">Informações Adicionais</TabsTrigger>
                  </TabsList>

                  <TabsContent value="personal" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Informações Básicas</CardTitle>
                        <CardDescription>
                          Dados de identificação do cliente
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nome Completo</FormLabel>
                              <FormControl>
                                <Input placeholder="Nome completo do cliente" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Telefone</FormLabel>
                                <FormControl>
                                  <Input placeholder="(00) 00000-0000" {...field} />
                                </FormControl>
                                <FormDescription>
                                  Usado para buscar o cliente durante o checkout
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="email@exemplo.com" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="customer_type"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Tipo de Cliente</FormLabel>
                                <Select 
                                  onValueChange={field.onChange} 
                                  defaultValue={field.value}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Selecione o tipo" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="pessoa_fisica">Pessoa Física</SelectItem>
                                    <SelectItem value="pessoa_juridica">Pessoa Jurídica</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="document"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {form.watch('customer_type') === 'pessoa_fisica' ? 'CPF' : 'CNPJ'}
                                </FormLabel>
                                <FormControl>
                                  <Input 
                                    placeholder={form.watch('customer_type') === 'pessoa_fisica' ? '000.000.000-00' : '00.000.000/0000-00'} 
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="birthday"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Data de Nascimento</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="address" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Endereço</CardTitle>
                        <CardDescription>
                          Informações de localização do cliente
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FormField
                          control={form.control}
                          name="address"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Endereço Completo</FormLabel>
                              <FormControl>
                                <Input placeholder="Rua, número, complemento, bairro" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name="city"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Cidade</FormLabel>
                                <FormControl>
                                  <Input placeholder="Cidade" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="state"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Estado</FormLabel>
                                <Select 
                                  onValueChange={field.onChange} 
                                  defaultValue={field.value}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="UF" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="AC">AC</SelectItem>
                                    <SelectItem value="AL">AL</SelectItem>
                                    <SelectItem value="AP">AP</SelectItem>
                                    <SelectItem value="AM">AM</SelectItem>
                                    <SelectItem value="BA">BA</SelectItem>
                                    <SelectItem value="CE">CE</SelectItem>
                                    <SelectItem value="DF">DF</SelectItem>
                                    <SelectItem value="ES">ES</SelectItem>
                                    <SelectItem value="GO">GO</SelectItem>
                                    <SelectItem value="MA">MA</SelectItem>
                                    <SelectItem value="MT">MT</SelectItem>
                                    <SelectItem value="MS">MS</SelectItem>
                                    <SelectItem value="MG">MG</SelectItem>
                                    <SelectItem value="PA">PA</SelectItem>
                                    <SelectItem value="PB">PB</SelectItem>
                                    <SelectItem value="PR">PR</SelectItem>
                                    <SelectItem value="PE">PE</SelectItem>
                                    <SelectItem value="PI">PI</SelectItem>
                                    <SelectItem value="RJ">RJ</SelectItem>
                                    <SelectItem value="RN">RN</SelectItem>
                                    <SelectItem value="RS">RS</SelectItem>
                                    <SelectItem value="RO">RO</SelectItem>
                                    <SelectItem value="RR">RR</SelectItem>
                                    <SelectItem value="SC">SC</SelectItem>
                                    <SelectItem value="SP">SP</SelectItem>
                                    <SelectItem value="SE">SE</SelectItem>
                                    <SelectItem value="TO">TO</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="postal_code"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>CEP</FormLabel>
                                <FormControl>
                                  <Input placeholder="00000-000" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="additional" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Informações Adicionais</CardTitle>
                        <CardDescription>
                          Notas e observações sobre o cliente
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <FormField
                          control={form.control}
                          name="notes"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Observações</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Preferências, histórico ou outras informações relevantes" 
                                  className="min-h-[150px]"
                                  {...field} 
                                />
                              </FormControl>
                              <FormDescription>
                                Informações internas que podem ajudar no atendimento
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Ações</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      type="submit" 
                      className="w-full mb-4" 
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      {isEditing ? 'Atualizar Cliente' : 'Salvar Cliente'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => navigate('/dashboard/customers')}
                    >
                      Cancelar
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Informações de Cadastro</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center">
                      <User className="text-muted-foreground mr-2 h-4 w-4" />
                      <span className="text-sm text-muted-foreground">
                        {form.watch('name') || 'Nome não informado'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="text-muted-foreground mr-2 h-4 w-4" />
                      <span className="text-sm text-muted-foreground">
                        {form.watch('phone') || 'Telefone não informado'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="text-muted-foreground mr-2 h-4 w-4" />
                      <span className="text-sm text-muted-foreground">
                        {form.watch('email') || 'Email não informado'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Home className="text-muted-foreground mr-2 h-4 w-4" />
                      <span className="text-sm text-muted-foreground">
                        {form.watch('address') || 'Endereço não informado'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </DashboardLayout>
  );
};

export default CustomerForm; 